import api from './api';

/**
 * Service for coupon-related API calls
 */

/**
 * Fetch all coupons
 * @param {Object} filters - Optional filters for coupons
 * @returns {Promise<Array>} Array of coupon data
 */
export const fetchAllCoupons = async (filters = {}) => {
  try {
    const response = await api.get('/coupons', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching coupons:', error);
    throw error;
  }
};

/**
 * Fetch active coupons
 * @returns {Promise<Array>} Array of active coupon data
 */
export const fetchActiveCoupons = async () => {
  try {
    const response = await api.get('/coupons/active');
    return response.data;
  } catch (error) {
    console.error('Error fetching active coupons:', error);
    throw error;
  }
};

/**
 * Fetch a coupon by ID
 * @param {string} couponId - The ID of the coupon to fetch
 * @returns {Promise<Object>} Coupon data
 */
export const fetchCouponById = async (couponId) => {
  try {
    const response = await api.get(`/coupons/${couponId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching coupon ${couponId}:`, error);
    throw error;
  }
};

/**
 * Fetch a coupon by code
 * @param {string} couponCode - The code of the coupon to fetch
 * @returns {Promise<Object>} Coupon data
 */
export const fetchCouponByCode = async (couponCode) => {
  try {
    const response = await api.get(`/coupons/code/${couponCode}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching coupon with code ${couponCode}:`, error);
    throw error;
  }
};

/**
 * Validate a coupon for a specific booking type and amount
 * @param {Object} validationData - Data for coupon validation
 * @param {string} validationData.code - The coupon code to validate
 * @param {string} validationData.bookingType - The type of booking
 * @param {number} validationData.amount - The booking amount
 * @returns {Promise<Object>} Validation result with discount information
 */
export const validateCoupon = async (validationData) => {
  try {
    const response = await api.post('/coupons/validate', validationData);
    return response.data;
  } catch (error) {
    console.error('Error validating coupon:', error);
    throw error;
  }
};

/**
 * Create a new coupon (admin only)
 * @param {Object} couponData - Data for the new coupon
 * @returns {Promise<Object>} Created coupon data
 */
export const createCoupon = async (couponData) => {
  try {
    // Format dates if they are Date objects
    const formattedData = {
      ...couponData,
      validFrom: couponData.validFrom instanceof Date 
        ? couponData.validFrom.toISOString() 
        : couponData.validFrom,
      validUntil: couponData.validUntil instanceof Date 
        ? couponData.validUntil.toISOString() 
        : couponData.validUntil,
    };

    const response = await api.post('/coupons', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error creating coupon:', error);
    throw error;
  }
};

/**
 * Update a coupon (admin only)
 * @param {string} couponId - The ID of the coupon to update
 * @param {Object} updateData - Data to update the coupon with
 * @returns {Promise<Object>} Updated coupon data
 */
export const updateCoupon = async (couponId, updateData) => {
  try {
    // Format dates if they are Date objects
    const formattedData = {
      ...updateData,
      validFrom: updateData.validFrom instanceof Date 
        ? updateData.validFrom.toISOString() 
        : updateData.validFrom,
      validUntil: updateData.validUntil instanceof Date 
        ? updateData.validUntil.toISOString() 
        : updateData.validUntil,
    };

    const response = await api.patch(`/coupons/${couponId}`, formattedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating coupon ${couponId}:`, error);
    throw error;
  }
};

/**
 * Delete a coupon (admin only)
 * @param {string} couponId - The ID of the coupon to delete
 * @returns {Promise<void>}
 */
export const deleteCoupon = async (couponId) => {
  try {
    await api.delete(`/coupons/${couponId}`);
  } catch (error) {
    console.error(`Error deleting coupon ${couponId}:`, error);
    throw error;
  }
};
